import React, { useState, useEffect } from 'react';

import './AddActivityModal.css';
import { supabase } from '../../services/supabaseClient/supabaseClient';
import { TasksAgentService } from '../../services/llmClient/TasksAgentService';

const AddActivityModal = ({ onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    title: '',
    owner: '',
    date: '',
    priority: 'medium',
    status: 'todo',
    projectId: '',
    courseId: '',
    description: '',
    reflection: '',
    conversationSummary: '',
    estimatedMinutes: '',
    tags: '',
    isPinned: false,
    cardColor: '',
    icon: '',
  });
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [aiEnhanced, setAiEnhanced] = useState(false);
  const [projects, setProjects] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  // Fetch projects and courses on mount
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!isMounted) return;

        if (session?.user) {
          // Fetch both in parallel for better performance
          const [projectsResult, coursesResult] = await Promise.all([
            supabase
              .from('ml_projects_staging')
              .select('id, project_name')
              .eq('user_id', session.user.id)
              .order('project_name'),
            supabase
              .from('ml_courses_staging')
              .select('id, title')
              .eq('owner_id', session.user.id)
              .order('title'),
          ]);

          if (!isMounted) return;

          // Only update state if component is still mounted
          if (!projectsResult.error) {
            setProjects(projectsResult.data || []);
          }

          if (!coursesResult.error) {
            setCourses(coursesResult.data || []);
          }
        }
      } catch (error) {
        // Silently handle errors to prevent crashes
        if (isMounted) {
          setProjects([]);
          setCourses([]);
        }
      } finally {
        if (isMounted) {
          setLoadingData(false);
        }
      }
    };

    fetchData();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEnhanceWithAI = async () => {
    if (!formData.title.trim()) {
      alert('Please enter at least a task title before enhancing with AI');
      return;
    }

    setIsEnhancing(true);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      // Build description from form data
      const contextDescription = `${formData.title}. ${formData.description || ''}`;

      const result = await TasksAgentService.createTaskFromNaturalLanguage({
        userId: session?.user?.id || 'unknown-user',
        description: contextDescription,
        currentGoals: [],
        currentCourses: courses.map((c) => c.course_name),
        existingTasks: [],
        userPreferences: {},
      });

      const enhancedTask = result.task;

      // Enhance existing form data with AI suggestions
      setFormData((prev) => ({
        ...prev,
        description: enhancedTask.description || prev.description,
        why: enhancedTask.why || prev.why,
        how: enhancedTask.how || prev.how,
        owner: enhancedTask.who || prev.owner,
        priority: enhancedTask.urgencyLevel || prev.priority,
      }));

      setAiEnhanced(true);
      alert('Task enhanced with AI!');
    } catch (error) {
      alert('AI enhancement is unavailable. Please try again.');
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.date) {
      alert('Please fill in at least "Title" and "Date" fields');
      return;
    }

    try {
      // Use current time with the selected date
      const now = new Date();
      const currentTime = now.toTimeString().slice(0, 5); // HH:MM format
      const when = `${formData.date}T${currentTime}`;

      onAdd({
        what: formData.title,
        title: formData.title,
        who: formData.owner,
        owner: formData.owner,
        description: formData.description || `Task: ${formData.title}`,
        taskDescription: formData.description || `Task: ${formData.title}`,
        when,
        deadline: when,
        urgencyLevel: formData.priority,
        priority: formData.priority,
        status: formData.status,
        projectId: formData.projectId,
        courseId: formData.courseId,
        aiGenerated: aiEnhanced,
        reflection: formData.reflection || '',
        conversationSummary: formData.conversationSummary || '',
        estimatedMinutes: formData.estimatedMinutes ? parseInt(formData.estimatedMinutes) : null,
        tags: formData.tags
          ? formData.tags
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean)
          : [],
        isPinned: formData.isPinned,
        cardColor: formData.cardColor || null,
        icon: formData.icon || null,
        scratchPad: '',
        timerPaused: false,
      });
    } catch (error) {
      alert('Failed to create task. Please try again.');
    }
  };

  return (
    <div
      className="add-activity-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
      role="button"
      tabIndex={0}
      aria-label="Close modal"
    >
      <div className="add-activity-modal" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2 className="modal-title">
            <i className="fa-solid fa-plus-circle modal-header-icon" aria-label="New Task" />
            New Task
          </h2>
          <button type="button" onClick={onClose} className="modal-close-btn" aria-label="Close">
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="modal-form-content">
            <div className="ai-enhance-section">
              <button
                type="button"
                onClick={handleEnhanceWithAI}
                className="btn-enhance"
                disabled={isEnhancing || !formData.title.trim()}
                title="Use AI to enhance your task with suggestions"
              >
                {isEnhancing ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                    Enhancing...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true" />
                    {aiEnhanced ? 'Re-enhance with AI' : 'Enhance with AI'}
                  </>
                )}
              </button>
            </div>

            <div className="form-section required">
              <label
                htmlFor="title"
                className="form-label icon-label"
                title="Title *"
                aria-label="Title"
              >
                <i className="fa-solid fa-heading" aria-hidden="true" />
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Task title *"
                className="form-input"
                required
              />
            </div>

            <div className="form-section">
              <label
                htmlFor="description"
                className="form-label icon-label"
                title="Description"
                aria-label="Description"
              >
                <i className="fa-solid fa-align-left" aria-hidden="true" />
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Task description (optional)"
                className="form-input"
                rows="3"
              />
            </div>

            <div className="form-section">
              <label
                htmlFor="owner"
                className="form-label icon-label"
                title="Owner"
                aria-label="Owner"
              >
                <i className="fa-solid fa-user" aria-hidden="true" />
              </label>
              <input
                type="text"
                id="owner"
                name="owner"
                value={formData.owner}
                onChange={handleChange}
                placeholder="Task owner (optional)"
                className="form-input"
              />
            </div>

            <div className="form-section required">
              <label
                htmlFor="date"
                className="form-label icon-label"
                title="Date *"
                aria-label="Date"
              >
                <i className="fa-regular fa-calendar" aria-hidden="true" />
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className="form-input date-picker"
                required
              />
            </div>

            <div className="form-section">
              <label
                htmlFor="priority"
                className="form-label icon-label"
                title="Priority"
                aria-label="Priority"
              >
                <i className="fa-solid fa-flag" aria-hidden="true" />
              </label>
              <select
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="form-input"
              >
                <option value="low">🟢 Low</option>
                <option value="medium">🟡 Medium</option>
                <option value="high">🟠 High</option>
                <option value="critical">🔴 Critical</option>
              </select>
            </div>

            <div className="form-section">
              <label
                htmlFor="status"
                className="form-label icon-label"
                title="Status"
                aria-label="Status"
              >
                <i className="fa-solid fa-tasks" aria-hidden="true" />
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="form-input"
              >
                <option value="todo">📋 To Do</option>
                <option value="in_progress">⚙️ In Progress</option>
                <option value="done">✅ Done</option>
              </select>
            </div>

            <div className="form-section">
              <label
                htmlFor="estimatedMinutes"
                className="form-label icon-label"
                title="Estimated Time (minutes)"
                aria-label="Estimated Time"
              >
                <i className="fa-solid fa-clock" aria-hidden="true" />
              </label>
              <input
                type="number"
                id="estimatedMinutes"
                name="estimatedMinutes"
                value={formData.estimatedMinutes}
                onChange={handleChange}
                placeholder="Est. time (minutes)"
                className="form-input"
                min="1"
              />
            </div>

            <div className="form-section">
              <label
                htmlFor="tags"
                className="form-label icon-label"
                title="Tags (comma-separated)"
                aria-label="Tags"
              >
                <i className="fa-solid fa-tags" aria-hidden="true" />
              </label>
              <input
                type="text"
                id="tags"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder="Tags (comma-separated)"
                className="form-input"
              />
            </div>

            <div className="form-section">
              <label
                htmlFor="projectId"
                className="form-label icon-label"
                title="Project"
                aria-label="Project"
              >
                <i className="fa-solid fa-diagram-project" aria-hidden="true" />
              </label>
              <select
                id="projectId"
                name="projectId"
                value={formData.projectId}
                onChange={handleChange}
                className="form-input"
                disabled={loadingData}
              >
                <option value="">No Project</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.project_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-section">
              <label
                htmlFor="courseId"
                className="form-label icon-label"
                title="Course"
                aria-label="Course"
              >
                <i className="fa-solid fa-graduation-cap" aria-hidden="true" />
              </label>
              <select
                id="courseId"
                name="courseId"
                value={formData.courseId}
                onChange={handleChange}
                className="form-input"
                disabled={loadingData}
              >
                <option value="">No Course</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-section">
              <label
                htmlFor="cardColor"
                className="form-label icon-label"
                title="Card Color"
                aria-label="Card Color"
              >
                <i className="fa-solid fa-palette" aria-hidden="true" />
              </label>
              <select
                id="cardColor"
                name="cardColor"
                value={formData.cardColor}
                onChange={handleChange}
                className="form-input"
              >
                <option value="">Default</option>
                <option value="blue">🔵 Blue</option>
                <option value="green">🟢 Green</option>
                <option value="yellow">🟡 Yellow</option>
                <option value="red">🔴 Red</option>
                <option value="purple">🟣 Purple</option>
                <option value="pink">🩷 Pink</option>
              </select>
            </div>

            <div className="form-section">
              <label
                htmlFor="icon"
                className="form-label icon-label"
                title="Icon"
                aria-label="Icon"
              >
                <i className="fa-solid fa-icons" aria-hidden="true" />
              </label>
              <select
                id="icon"
                name="icon"
                value={formData.icon}
                onChange={handleChange}
                className="form-input"
              >
                <option value="">No Icon</option>
                <option value="star">⭐ Star</option>
                <option value="fire">🔥 Fire</option>
                <option value="rocket">🚀 Rocket</option>
                <option value="lightbulb">💡 Lightbulb</option>
                <option value="target">🎯 Target</option>
                <option value="book">📚 Book</option>
                <option value="code">💻 Code</option>
                <option value="pencil">✏️ Pencil</option>
              </select>
            </div>

            <div className="form-section checkbox-section">
              <label htmlFor="isPinned" className="checkbox-label">
                <input
                  type="checkbox"
                  id="isPinned"
                  name="isPinned"
                  checked={formData.isPinned}
                  onChange={(e) => setFormData((prev) => ({ ...prev, isPinned: e.target.checked }))}
                  className="form-checkbox"
                />
                <i className="fa-solid fa-thumbtack" aria-hidden="true" />
                <span>Pin this task</span>
              </label>
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-cancel" aria-label="Cancel">
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
            <button type="submit" className="btn-submit" aria-label="Add task">
              <i className="fa-solid fa-check" aria-hidden="true" />
              Add Task
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddActivityModal;
